
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift as GiftIcon, ChevronRight } from 'lucide-react';
import type { Gift, Brand } from '@/utils/types';

interface GiftCardProps {
  gift: Gift;
  brand: Brand;
  onSelect?: () => void;
  onRedeem?: () => void;
  hideAmount?: boolean; // Added this prop to optionally hide the amount
}

const GiftCard: React.FC<GiftCardProps> = ({ 
  gift, 
  brand, 
  onSelect, 
  onRedeem,
  hideAmount = false // Default to showing the amount
}) => {
  const gradientColors = {
    restaurants: 'from-green-100 to-green-200',
    beverages: 'from-orange-100 to-orange-200',
    convenience: 'from-blue-100 to-blue-200',
    entertainment: 'from-purple-100 to-purple-200',
  };

  const gradientClass = gradientColors[brand.silo];

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.onerror = null; // Prevent infinite error loop
    e.currentTarget.style.display = 'none'; // Hide the broken image
    e.currentTarget.parentElement?.classList.add('fallback-icon-visible');
  };

  return (
    <Card className={`gift-card overflow-hidden bg-gradient-to-br ${gradientClass} hover:shadow-lg transition-all duration-300 h-full`}>
      <CardHeader className="pb-2 pt-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mr-3 relative fallback-icon-container shadow-sm">
              {brand.logo ? (
                <>
                  <img 
                    src={brand.logo} 
                    alt={brand.name} 
                    className="w-10 h-10 object-contain"
                    onError={handleImageError}
                  />
                  <GiftIcon className="h-5 w-5 text-givgo-purple absolute fallback-icon" />
                </>
              ) : (
                <GiftIcon className="h-7 w-7 text-givgo-purple" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-xl">{brand.name}</h3>
              <p className="text-sm text-muted-foreground">GivGo Bucks</p>
            </div>
          </div>
          {!hideAmount && (
            <div className="text-2xl font-bold text-givgo-dark">${gift.amount}</div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {gift.message && (
          <div className="bg-white/80 p-3 rounded-lg mt-2 text-sm italic">
            "{gift.message}"
          </div>
        )}
      </CardContent>
      <CardFooter className="border-t bg-white/40 flex justify-between">
        <div className="text-xs text-muted-foreground">
          {gift.isRedeemed ? 'Redeemed' : 'Ready to use'}
        </div>
        {onSelect && (
          <Button variant="ghost" size="sm" onClick={onSelect} className="text-givgo-purple">
            <span>Select</span>
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
        {onRedeem && !gift.isRedeemed && (
          <Button size="sm" onClick={onRedeem} className="bg-givgo-purple hover:bg-givgo-purple/90">
            Redeem
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default GiftCard;
